import { useController, type FieldValues } from 'react-hook-form';
import { Form } from 'semantic-ui-react';

import { FieldError } from '@components/form/fields';
import type { BaseFieldProps } from '@components/form/fields/field.types';

import {
  AssetTypeDropdown,
  type AssetTypeDropdownProps,
} from './asset-type-dropdown';

export type AssetTypeDropdownFieldProps<T extends FieldValues = FieldValues> =
  BaseFieldProps<T> & Omit<AssetTypeDropdownProps, 'onChange' | 'value'>;

/**
 * Asset type dropdown wired to React Hook Form via `useController`.
 *
 * @see AssetTypeDropdown
 */
export const AssetTypeDropdownField = <T extends FieldValues>({
  className,
  control,
  label,
  name,
  required,
  width,
  ...rest
}: AssetTypeDropdownFieldProps<T>) => {
  const { field, fieldState } = useController({ control, name });
  const id = rest.id ?? name;
  const errorId = `${id}-error`;
  const hasError = !!fieldState.error;

  return (
    <Form.Field
      className={className}
      error={hasError}
      required={required}
      width={width}
    >
      {label && <label htmlFor={id}>{label}</label>}

      <AssetTypeDropdown
        {...rest}
        ref={field.ref}
        aria-errormessage={hasError ? errorId : undefined}
        aria-invalid={hasError || undefined}
        id={id}
        onBlur={field.onBlur}
        onChange={field.onChange}
        value={field.value ?? ''}
      />

      <FieldError id={errorId} message={fieldState.error?.message} />
    </Form.Field>
  );
};
